import {createAsyncThunk} from '@reduxjs/toolkit';

import {ILookupEntry} from 'shared/types/lookups';
import {TAppState} from 'store/config/types';
import {IReportConfigurationAttribute} from 'store/slices/report-configuration-slice';
import {fetchTableReportFilterConditions} from 'store/slices/report-configuration-slice/dd-lookups/dd-lookups-api';
import {getTableAttributes} from 'store/slices/report-configuration-slice/report-dependencies';
import {
    deleteTableColumnFilters, TDeleteTableColumnFiltersArgs,
    fetchTableColumnFilters,
    TFetchTableColumnFiltersArgs,
    postTableColumnFilters,
    TPostTableColumnFiltersArgs,
    updateTableColumnFilters,
    TUpdateTableColumnFiltersArgs,
} from 'store/slices/table-report-slice/table-report-column-filters/table-report-column-filters-api';
import {ITableReportColumnFilerDto} from 'store/slices/table-report-slice/table-report-column-filters/table-report-column-filters-types';

import {fetchTableReportFiltersConditions} from '../table-report-slice-api';
import {TABLE_REPORT_SLICE} from '../table-report-slice-constants';
import {selectTableReportAttributes, selectTableReportFilterConditions} from '../table-report-slice-selectors';

export interface ILoadTableReportConfigurationFilterConditionsWithColumnLookupsArgs {
    templateCode: string;
}

export const loadTableReportFilterConditions = createAsyncThunk<
    ILookupEntry[], void
>(
    `${TABLE_REPORT_SLICE}/loadTableReportFilterConditions`,
    async (args, {rejectWithValue}) => {
        try {
            const response = await fetchTableReportFiltersConditions();
            return response.data;
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const loadTableReportFilterConditionsWithAttributes = createAsyncThunk<
    {
        filterConditions: ILookupEntry[];
        reportAttributes: IReportConfigurationAttribute[] | undefined;
    },
    {templateCode: string}>(
        `${TABLE_REPORT_SLICE}/loadTableReportFilterConditionsWithColumns `,
        async (args, {rejectWithValue, getState}) => {
            try {
                const {templateCode} = args;

                const tableReportFiltersInStore = selectTableReportFilterConditions(
                    getState() as TAppState,
                );
                const reportColumnsInStore = !templateCode ? undefined
                    : selectTableReportAttributes(
                        getState() as TAppState,
                    );

                const loadRequests = await Promise.all([
                    await (tableReportFiltersInStore ? Promise.resolve(
                        {data: tableReportFiltersInStore},
                    ) : fetchTableReportFilterConditions()),
                    await (reportColumnsInStore ? Promise.resolve({data: reportColumnsInStore})
                        : templateCode ? getTableAttributes({
                            templateCode,
                        }) : Promise.resolve({data: undefined as IReportConfigurationAttribute[] | undefined})),
                ]);

                const [
                    tableReportFiltersResponse,
                    childReportColumnsResponse,
                ] = loadRequests;

                return {
                    filterConditions: tableReportFiltersResponse.data,
                    reportAttributes: childReportColumnsResponse.data,
                };
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

interface ITableReportColumnSavedFiltersData {
    columnName: string;
    filters: ITableReportColumnFilerDto[];
}

export const loadTableReportColumnFilters = createAsyncThunk<
    ITableReportColumnSavedFiltersData, TFetchTableColumnFiltersArgs
    >(
        `${TABLE_REPORT_SLICE}/loadTableReportColumnFilters`,
        async (args, {rejectWithValue}) => {
            try {
                const {columnName} = args;
                const response = await fetchTableColumnFilters(args);
                const {data} = response;
                return {
                    columnName,
                    filters: data,
                };
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const saveTableReportColumnFilters = createAsyncThunk<
    string, TPostTableColumnFiltersArgs
    >(
        `${TABLE_REPORT_SLICE}/saveTableReportColumnFilters`,
        async (args, {rejectWithValue}) => {
            try {
                const response = await postTableColumnFilters(args);
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const updateTableReportColumnFilters = createAsyncThunk<
    string, TUpdateTableColumnFiltersArgs
    >(
        `${TABLE_REPORT_SLICE}/updateTableReportColumnFilters`,
        async (args, {rejectWithValue}) => {
            try {
                const response = await updateTableColumnFilters(args);
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const removeTableReportColumnFilters = createAsyncThunk<
    string, TDeleteTableColumnFiltersArgs
    >(
        `${TABLE_REPORT_SLICE}/removeTableReportColumnFilters`,
        async (args, {rejectWithValue}) => {
            try {
                const response = await deleteTableColumnFilters(args);
                const {data} = response;
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );
